import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcTradeV1TradeQueryServiceTradeGet } from '@interxfi/api-client/generated/hooks/ixc-trade-v1-trade-query-service-trade-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcTradeV1TradeState } from '@interxfi/types/generated/api';
import { UtcTradeSidePill } from '../../../../../../../../../../features/dtcc/utc-trade/utc-side-pill';
import { OrderSidePill } from '../../../../../../../../../../features/order/order-side-pill';
import { TradeEventDrawer, TradeEventSummary } from '../../../../../../../../../../features/trade/trade-event';
import { getTradeStateListItems } from '../../../../../../../../../../features/trade/trade-list-items';
import { getTradeDetailPath } from '../../../../../../../../../../features/trade/trade-routing';
import { TradeStatusPill } from '../../../../../../../../../../features/trade/trade-status-pill';
import { TradeSummaryBar } from '../../../../../../../../../../features/trade/trade-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/trade/utc/_utc/$tradeId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `utc-trade-${params.tradeId}`,
        label: 'Trade',
        to: getTradeDetailPath(params.tradeId),
        suffix: <CopyButton content={params.tradeId} label="Copy Trade ID" tooltip={{ copy: params.tradeId, copied: 'Copied Trade ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('UTC Trade'),
  component: TradeDetail,
});

function getListItems(data: IxcTradeV1TradeState | undefined) {
  const items = getTradeStateListItems(data);

  return {
    general: [items.metadata.createdAt, items.metadata.updatedAt, items.data.orderId, items.tradeId, items.executionId] satisfies ReadonlyListItem[],
    route: items.data.route
      ? {
          left: [
            {
              id: 'main',
              items: [
                items.data.route.symbol,
                items.data.route.cusip,
                items.data.route.sidePill,
                items.data.route.quantity,
                items.data.route.price,
                items.data.route.priceSource,
                items.data.route.grossAmount,
                items.data.route.netAmount,
              ],
            },
            {
              id: 'ext',
              items: [items.data.route.solicited, items.data.route.trailer, items.data.route.capacity, items.data.route.market],
            },
            ...(items.data.route.middleMoneyAsKeyValuePairs.length
              ? [
                  {
                    id: 'middleMoney',
                    title: 'Middle Money',
                    items: items.data.route.middleMoneyAsKeyValuePairs,
                  },
                ]
              : []),
          ] satisfies ReadonlyListGroup[],
          right: [
            items.data.route.executionTime,
            items.data.route.tradeDate,
            items.data.route.settlementDate,
            items.data.route.orderId,
            items.data.route.accountId,
            items.data.route.executionId,
            items.data.route.assetId,
          ] satisfies ReadonlyListItem[],
        }
      : undefined,
    firmRoute: items.data.firmRoute
      ? {
          left: [
            {
              id: 'main',
              items: [
                items.data.firmRoute.symbol,
                items.data.firmRoute.cusip,
                items.data.firmRoute.sidePill,
                items.data.firmRoute.quantity,
                items.data.firmRoute.price,
                items.data.firmRoute.grossAmount,
              ],
            },
          ] satisfies ReadonlyListGroup[],
          right: [
            items.data.firmRoute.executionTime,
            items.data.firmRoute.tradeDate,
            items.data.firmRoute.settlementDate,
            items.data.firmRoute.firmOrderId,
            items.data.firmRoute.executionId,
            items.data.firmRoute.assetId,
          ] satisfies ReadonlyListItem[],
        }
      : undefined,
    utc: items.data.utc
      ? {
          left: [
            items.data.utc.marketControlNumber,
            items.data.utc.symbol,
            items.data.utc.cusip,
            items.data.utc.sidePill,
            items.data.utc.quantity,
            items.data.utc.unitPrice,
            items.data.utc.grossAmount,
            items.data.utc.netSettlementAmount,
            items.data.utc.nettingIndicator,
            items.data.utc.securityType,
            items.data.utc.settlementType,
          ] satisfies ReadonlyListItem[],
          right: [
            items.data.utc.tradeDate,
            items.data.utc.tradeTime,
            items.data.utc.settlementDate,
            items.data.utc.orderId,
            items.data.utc.clientOrderId,
            items.data.utc.executionId,
          ] satisfies ReadonlyListItem[],
        }
      : undefined,
  } as const;
}

function getMenuItems(tradeId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: TradeEventDrawer,
        props: { request: { tradeId } },
      },
    },
    actions: [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load trade', 'view this trade');

function TradeDetail() {
  const { tradeId } = Route.useParams();
  const request = { tradeId } as const;
  const { data, error, isPending } = useIxcTradeV1TradeQueryServiceTradeGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.trade), [data?.trade]);
  const menuItems = useMemo(() => getMenuItems(tradeId), [tradeId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <TradeStatusPill isPending={showPending} size="large" status={data?.trade?.status} />
            {match({ data: data?.trade?.data, showPending })
              .with(P.union({ showPending: true }, { data: { utc: { side: P.not(P.nullish) } } }), (c) => (
                <UtcTradeSidePill isPending={c.showPending} side={c.data?.utc?.side} size="large" />
              ))
              .with({ data: { route: { side: P.not(P.nullish) } }, showPending }, (c) => <OrderSidePill side={c.data.route.side} size="large" />)
              .otherwise(() => null)}
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <TradeSummaryBar tradeId={tradeId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <TradeEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>General</Typography.Level3>
          <AppReadonlyList isPending={showPending} items={listItems.general} />
        </VerticalStack>
      </PageLayout.Section>

      {listItems.route ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Route</Typography.Level3>

            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <AppReadonlyListStack groups={listItems.route.left} isPending={showPending} />,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <BoxedAppReadonlyList isPending={showPending} items={listItems.route.right} />,
              }}
              wrap="forward"
              wrapGap={7}
            />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {listItems.firmRoute ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Firm Route</Typography.Level3>

            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <AppReadonlyListStack groups={listItems.firmRoute.left} isPending={showPending} />,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <BoxedAppReadonlyList isPending={showPending} items={listItems.firmRoute.right} />,
              }}
              wrap="forward"
              wrapGap={7}
            />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {listItems.utc ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>UTC</Typography.Level3>

            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <AppReadonlyList isPending={showPending} items={listItems.utc.left} />,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <BoxedAppReadonlyList isPending={showPending} items={listItems.utc.right} />,
              }}
              wrap="forward"
              wrapGap={7}
            />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
